define('bindingHandlers/accordion2/accordion2',[
    'jquery',
    'lib/lodash4',
    'lib/knockout',
    'text!bindingHandlers/accordion2/templates/header.html',
    'text!bindingHandlers/accordion2/templates/body.html',
    'util/bindingHandlerUtils',

    // no reference required
    'bindingHandlers/accordion2/animateAccordion',
    'bindingHandlers/include',
    'extensions/i18n/knockout.lString'
], function ($,_, ko, ACCORDION_HEADER, ACCORDION_BODY, bindingHandlerUtils) {
    'use strict';

    /**
     *  Accordion2 Custom Binding will hide/show a dom element containing class accordion-body
     *  It will toggle hide/show a section when a click event occurs on the dom with class accordion-header
     *
     *  Binding Syntax is as follows
     *       data-bind = {
     *          allowMultiple: Optional, boolean; If true, drawers will not close when another is opened,
     *          guided: Optional, boolean; If true, will add guided state indicators to the header. See UX Spec for details on guided accordion.
     *          itemTypeProperty: Optional, string; The type on each item to use to determine the matching drawer template,
     *          items: Optional, array; A dynamic list of objects for which to create drawers in the accordion
     *       }
     *  Describing Drawers
     *       Describing drawers is designed to be easy and intuitive. Simply set up your binding on a root element,
     *       then describe each distinct drawer (or drawer type, for a dynamic list) with data attributes.
     *       Data attributes for drawer are as follows:
     *          data-with-context: optional. The property on the current context to bind the entire drawer
     *          data-body-data/data-header-data: optional. The property on the current to bind the body/header
     *          data-body-template/data-header-template: optional. The path to a template to use to render the body/header of the drawer
     *          data-body-key/data-header-key: optional. A localization string key to render as the body/header of the drawer
     *          data-isopen: optional. The property on the drawer context to bind the open state of the drawer.
     *                                  Set to "true" to init a drawer template to open without providing an open state observable.
     *          data-iscomplete: optional. The property on the drawer context to bind the complete state of the
     *              drawer (only applicable if guided is true on the binding)
     *          Inline Template: to render a template inline, just create an element inside the drawer for
     *                the template with data-role equal to "body" or "header" as applicable
     *
     *      For examples of drawer descriptions, see the test case examples in
     *          src/js/test/spec/bindingHandlers/accordion2/specTemplates.html
     **/
    ko.bindingHandlers.accordion2 = {
        init: function (element, valueAccessor, allBindings, viewModel, bindingContext) {
            //**************************************************************************
            // A drawerOptions is an object with all describing properties of the drawer
            // from the template. It corresponds exactly to the template markup
            //
            // itemsMap is an array of all the items being rendered as drawers with their
            // corresponding drawer definition, and (optionally) their anonymous openState observable.
            // an object in itemsMap is thus:
            //    {
            //        item: the item context for rendering every single drawer (fixed or dynamic)
            //        drawerOptions: the drawer definition for rendering that item
            //        openStateObservable: null, if drawerOptions.openState is provided, or an observable
            //                             for binding to the open state of the drawer
            //    }
            //**************************************************************************

            var options = valueAccessor(),
                $element = $(element),
                drawerStateSubscriptions = [],
                itemsMap = ko.observableArray(),
                typeProperty = ko.unwrap(options.itemTypeProperty),
                drawerDefinitions = [],
                allowMultiple = ko.unwrap(options.allowMultiple);

            $element.addClass('accordion');

            if(ko.unwrap(options.guided)) {
                $element.addClass('accordion-guided');
            }

            mapAllDrawers();

            var itemsComputed = ko.computed(createDrawerInteraction);
            var computedMap;
            if(options.items) {
                _.forEach(getDynamicDrawers(), initializeDrawer);

                // when changes to the original items array occur, map these to the drawer
                // so that itemsMap reflect the current state of the array
                computedMap = ko.computed(function () {
                    var mappedItems = ko.unwrap(options.items).map(function(item) {
                        var type = ko.unwrap(ko.unwrap(item)[typeProperty]);
                        var mappedItem = _.find(itemsMap(), { item: item });
                        if(mappedItem) {
                            return mappedItem;
                        }
                        var drawerOptions = getDrawerForType(type);
                        if(!drawerOptions.$element.hasClass('accordion-item')) {
                            initializeDrawer(drawerOptions);
                            drawerOptions = getDrawerForType(type);
                        }
                        return bindingHandlerUtils.decorateWithTemplateContext({ item: item, drawerOptions: drawerOptions }, bindingContext);
                    });
                    itemsMap(mappedItems);
                });

                // using a dynamic set, we've injected bindings for each item type, so use ko.foreach
                ko.applyBindingsToNode(element, {
                    foreach: {
                        data: itemsMap,
                        afterRender: function () {
                            // clean up the container so anything not transformed doesn't interfere with the binding
                            $element.find('>*:not(.accordion-item)').remove();
                        }
                    }
                });
            } else {
                _.forEach(getFixedDrawers(), initializeDrawer);

                // using a fixed set, we've injected bindings for each item. In order to support
                // anonymous open state observables, extend the binding context with a map of
                // anonymous openStateObservables for each item to use
                var innerBindingContext = bindingContext.extend(function () {
                    var openStateObservables = {};
                    _.forEach(itemsMap(), function(itemMap) {
                        openStateObservables[itemMap.drawerOptions.withContext] = itemMap.openStateObservable;
                    });
                    return { openStates: openStateObservables};
                });

                ko.applyBindingsToDescendants(innerBindingContext, element);
            }

            // On disposal, kill all drawer subscriptions and items computed
            ko.utils.domNodeDisposal.addDisposeCallback(element, function() {
                _.forEach(drawerStateSubscriptions, function (subscription) {
                    subscription.dispose();
                });

                itemsComputed.dispose();
                if (!_.isUndefined(computedMap)) {
                    computedMap.dispose();
                }
            });

            return { controlsDescendantBindings: true };

            // Utility functions (late defined for clarity of reading the source)

            function getDrawerOptions (drawerElement) {
                var $drawerElement = $(drawerElement);
                // use caching of drawer definitions so we have one defined for each distinct type
                var existingDrawerOptions = _.find(drawerDefinitions, function(drawerOptions) {
                    return drawerOptions.$element === $drawerElement;
                });
                if(existingDrawerOptions) {
                    return existingDrawerOptions;
                }
                var newDrawerOptions = {
                    withContext: $drawerElement.attr('data-with-context') || '$data',
                    bodyContext: $drawerElement.attr('data-body-data') || '$data',
                    headerContext: $drawerElement.attr('data-header-data') || '$data',
                    bodyTemplate: $drawerElement.attr('data-body-template'),
                    bodyKey: $drawerElement.attr('data-body-key'),
                    headerTemplate: $drawerElement.attr('data-header-template'),
                    headerKey: $drawerElement.attr('data-header-key'),
                    openState: $drawerElement.attr('data-isopen'),
                    guidedState: $drawerElement.attr('data-iscomplete'),
                    itemType: $drawerElement.attr('data-item-type'),
                    $element: $drawerElement
                };

                if (newDrawerOptions.itemType || typeProperty) {
                    newDrawerOptions.conditional = 'ko.unwrap(' + typeProperty + ') === "' + newDrawerOptions.itemType + '"';
                }

                drawerDefinitions.push(newDrawerOptions);
                return newDrawerOptions;
            }

            function getDrawerForType (type) {
                var drawerOptions = _.find(drawerDefinitions, function (drawerOptions) {
                    return !drawerOptions.itemType || drawerOptions.itemType === type;
                });
                if (!drawerOptions) {
                    throw 'Unable to find a drawer with "' + typeProperty + '" of "' + type + '"';
                }
                return drawerOptions;
            }

            function mapAllDrawers () {
                var $drawerElements = $element.find('> div');
                if ($drawerElements.length === 0) {
                    throw 'You must create at least one drawer template inside your accordion node';
                }
                $drawerElements.map(function (index, $drawerElement) {
                    getDrawerOptions($drawerElement);
                });
            }

            function getDynamicDrawers () {
                // only create drawers for each unique type
                var itemTypes = _.uniq(ko.unwrap(options.items).map(function (item) {
                    return ko.unwrap(ko.unwrap(item)[typeProperty]);
                }));
                return itemTypes.map(function (type) {
                    return getDrawerForType(type);
                });
            }

            function getFixedDrawers () {
                return drawerDefinitions.map(function (drawerOptions) {
                    var contextItem = drawerOptions.withContext === '$data' ? ko.unwrap(viewModel) : ko.unwrap(viewModel[drawerOptions.withContext]);
                    itemsMap.push({ item: contextItem, drawerOptions: drawerOptions });
                    return drawerOptions;
                });
            }

            function initializeDrawerHeader($drawerElement, drawerOptions, openState, index) {
                //Create the header
                var $header, $headerTemplate;
                if(!drawerOptions.headerTemplate && !drawerOptions.headerKey) {
                    $headerTemplate = $drawerElement.find("[data-role='header']");
                    if (!$headerTemplate || $headerTemplate.length === 0) {
                        throw 'You must specify a template, template path, or key for the drawer header';
                    }
                } else if (drawerOptions.headerTemplate) {
                    $headerTemplate = $('<!-- ko include: "' + drawerOptions.headerTemplate + '" --><!-- /ko -->');
                } else { // header key was passed
                    $headerTemplate = $('<span data-bind="lString: \'' + drawerOptions.headerKey + '\'"></span>');
                }

                //create the header template
                $header = $(ACCORDION_HEADER);
                $header.attr('data-bind', 'with: ' + drawerOptions.headerContext + ', animateAccordion: ' + openState);
                $header.appendTo($drawerElement);

                // set up guided state indicators
                if (ko.unwrap(options.guided)) {
                    //dynamic items need index bound to foreach index, fixed items need static number
                    if(options.items) {
                        $header.find('.accordion-guide-number').attr('data-bind', 'text: $index() + 1');
                    } else {
                        $header.find('.accordion-guide-number').html(index + 1);
                    }
                } else {
                    $header.find('.accordion-guide-indicator').remove();
                }
                // put the template into the header
                $headerTemplate.insertAfter($header.find('.accordion-open-indicator'));
            }

            function initializeDrawerBody($drawerElement, drawerOptions) {
                var $body, $bodyTemplate;
                if(!drawerOptions.bodyTemplate && !drawerOptions.bodyKey) {
                    $bodyTemplate = $drawerElement.find("[data-role='body']");
                    if (!$bodyTemplate || $bodyTemplate.length === 0) {
                        throw 'You must specify a template, template path, or key for the drawer body';
                    }
                } else if(drawerOptions.bodyTemplate) {
                    $bodyTemplate = $('<!-- ko include: "' + drawerOptions.bodyTemplate + '" --><!-- /ko-->');
                } else { // body key was passed
                    $bodyTemplate = $('<span data-bind="lString: \'' + drawerOptions.bodyKey + '\'"></span>');
                }

                //create the body template
                $body = $(ACCORDION_BODY);
                $body.attr('data-bind', 'with: ' + drawerOptions.bodyContext);
                $body.appendTo($drawerElement);

                $bodyTemplate.appendTo($body.find('.accordion-body'));
            }

            function initializeDrawer (drawerOptions, index) {
                var $drawerElement = drawerOptions.$element || $('<div></div>');
                $drawerElement.addClass('accordion-item accordion2');
                if(!drawerOptions.$element) {
                    $element.append($drawerElement);
                }

                var openState = drawerOptions.openState;
                if (!openState || openState === 'true' || openState === 'false') {
                    if (options.items) {
                        openState = '$parents[1].openStateObservable';
                    } else {
                        openState = '$parentContext.openStates[\'' + drawerOptions.withContext + '\']';
                    }
                }

                var drawerCssBinding = '';
                if(drawerOptions.guidedState) {
                    drawerCssBinding = 'css: { "is-complete": ' + drawerOptions.guidedState + '}';
                }

                $drawerElement.attr('data-bind', drawerCssBinding);

                initializeDrawerHeader($drawerElement, drawerOptions, openState, index);
                initializeDrawerBody($drawerElement, drawerOptions);

                // wrap the drawer in it's with-context
                var drawerOuterHTML = '<!-- ko with: ' + drawerOptions.withContext + ' -->' + $drawerElement[0].outerHTML + '<!-- /ko -->';
                // wrap the drawer in a conditional (to match drawer type, if necessary)
                if(drawerOptions.conditional) {
                    drawerOuterHTML = '<!-- ko if: ' + drawerOptions.conditional + ' -->' + drawerOuterHTML + '<!-- /ko -->';
                }
                // if this is a dynamic set, a foreach is rendered with the itemsMap list, not the items list, so give each item an "item" context
                if(options.items) {
                    drawerOuterHTML = '<!-- ko with: item -->' + drawerOuterHTML + '<!-- /ko -->';
                }
                $drawerElement.replaceWith(drawerOuterHTML);
            }

            //Helper function to get the openState observable for a drawer/item
            function getOpenStateObservable (itemMap) {
                var item = ko.unwrap(itemMap.item),
                    openStateProp = itemMap.drawerOptions.openState;
                if(item && openStateProp !== 'true' && openStateProp !== 'false' && item[openStateProp]) {
                    return item[itemMap.drawerOptions.openState];
                }
                return itemMap.openStateObservable;
            }

            //Manages creating and cleaning subscriptions for item openState observables
            function createDrawerInteraction () {
                // Clean up any existing subscriptions
                _.forEach(drawerStateSubscriptions, function(subscription) {
                    subscription.dispose();
                });
                drawerStateSubscriptions = [];

                //iterate over each item
                _.forEach(itemsMap(), function (itemMap) {
                    //get the open state observable property for the item
                    var openState = getOpenStateObservable(itemMap);
                    if (!ko.isObservable(openState)) {
                        // if the item doesn't have an openState observable specified, create one, and hold onto it in the map
                        itemMap.openStateObservable = ko.observable(itemMap.drawerOptions.openState === 'true');
                        openState = itemMap.openStateObservable;
                    }
                    // if allowMultiple not provided (or false), return. Otherwise, set up subscription on items to close other drawers
                    if(allowMultiple) {
                        return;
                    }
                    var subscription = openState.subscribe(function (isOpen) {
                        // if an item has been opened, close other items
                        if (isOpen) {
                            _.forEach(itemsMap(), function (otherItemMap) {
                                if(itemMap.item === otherItemMap.item) {
                                    return;
                                }
                                var otherDrawerOpenState = getOpenStateObservable(otherItemMap);
                                if(ko.isObservable(otherDrawerOpenState) && otherDrawerOpenState()) {
                                    otherDrawerOpenState(false);
                                }
                            });
                        }
                    });
                    //hang onto subscription so it can be cleaned up later
                    drawerStateSubscriptions.push(subscription);
                });
            }
        }
    };
});

